import { ListService } from "../list-service";
import { MetalWalletService } from "./metal_wallet.service";
import { CryptoWalletService } from "./crypto_wallet.service";
import { Injectable } from "@angular/core";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { TradableWalletService } from "./tradable_wallet.service";
import { Tradable } from "@models/interfaces/tradable";
import { IndexWalletService } from "./index_wallet.service";
import { Fiat } from "@models";
import { StockWalletService } from "./stock_wallet.service";
import { EtfWalletService } from "./etf_wallet.service";
import { TradableTransaction } from "@models/transaction";
import { DestructorService } from "@core-services/destructor.service";
import { WalletBalance } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.model";
import { map, take } from "rxjs/operators";
import { Observable } from "rxjs";
import { WalletsBalanceStatus } from "@modules/+new/core/wallets-balances/domain/entities/wallets-balance-status.model";
import { EtcWalletService } from "@main-services/wallet/etc_wallet.service";
import { LeveragedTokenWalletService } from "@main-services/wallet/leveraged_token_wallet.service";
import { SecurityTokenWalletService } from "@main-services/wallet/security_token_wallet.service";

@Injectable({
	providedIn: "root"
})
export class TradableWalletListService extends ListService<
	TradableWallet,
	TradableWalletService<TradableWallet, TradableTransaction, Tradable>
> {
	public name: string = ServiceNames.TRADABLE_WALLET_LIST;

	public constructor(
		protected initService: InitService,
		protected destructor: DestructorService,
		protected metalWalletService: MetalWalletService,
		protected cryptoWalletService: CryptoWalletService,
		protected indexWalletService: IndexWalletService,
		protected stockWalletService: StockWalletService,
		protected etfWalletService: EtfWalletService,
		protected etcWalletService: EtcWalletService,
		protected leveragedTokenWalletService: LeveragedTokenWalletService,
		protected securityTokenWalletService: SecurityTokenWalletService
	) {
		super(initService, destructor, name);

		this.addSubForList([
			metalWalletService,
			cryptoWalletService,
			indexWalletService,
			stockWalletService,
			etfWalletService,
			etcWalletService,
			leveragedTokenWalletService,
			securityTokenWalletService
		]);
	}

	public getCryptoBalancesStatus$ = (): Observable<WalletsBalanceStatus> =>
		this.cryptoWalletService.getCryptoBalancesStatus$();

	public getNonEmptyWallets(tradable: Tradable = null): TradableWallet[] {
		let ws = this.list;
		if (tradable != null) {
			ws = this.services.map((s) => s.getWalletsByTradableId(tradable.getId())).find((list) => list.length > 0);
			if (!ws) {
				return [];
			}
		}
		return ws.filter((w) => w.getBalance() > 0);
	}

	public getWalletsByTradableId(id: string): TradableWallet[] {
		return this.services.map((s) => s.getWalletsByTradableId(id)).find((list) => list.length > 0);
	}

	// added because the above function doesn't work sometimes for some reason
	public getWalletsForTradable(tradableId: string): TradableWallet[] {
		return this.list.filter((w) => w.getCurrencyId() === tradableId);
	}

	public getCoinWalletsBalances(coinId: string): Promise<WalletsBalanceStatus> {
		const wallets = this.getWalletsByTradableId(coinId);
		const walletsIds = wallets.map((wallet) => wallet.getId());

		return this.getCryptoBalancesStatus$()
			.pipe(
				take(1),
				map((status: WalletsBalanceStatus) => ({
					...status,
					balances: status.balances.filter((balance: WalletBalance) =>
						walletsIds.some((id: string) => id === balance.walletGuid)
					)
				}))
			)
			.toPromise();
	}

	public getItemById(id: string, deleted: boolean = false): TradableWallet {
		let wallet = null;
		this.services.forEach((s) => {
			if (wallet) {
				return;
			}
			wallet = s.getItemById(id, deleted);
		});
		return wallet;
	}

	public getServiceForIndexInit(): IndexWalletService {
		return this.indexWalletService;
	}

	public getSumAllWalletsInFiat(fiat: Fiat): number {
		return this.services.reduce((sum, service) => sum + service.getSumOfWalletsInFiat(fiat), 0);
	}

	public getHighestBalanceWallet(tradableId: string): TradableWallet {
		const list = this.getWalletsByTradableId(tradableId);
		if (!list || list.length === 0) {
			return null;
		}
		return list.sort((w1: TradableWallet, w2: TradableWallet) => w2.getBalance() - w1.getBalance())[0];
	}
}
